// App-wide Olene session (replaces react-oidc-context).
//
// useAuth() exposes what the app already used from react-oidc-context:
//   isLoading, isAuthenticated, error, user.{access_token, id_token, profile, expires_at}
// plus:
//   signOut()        ends the app session and calls cognitoUser.signOut()
//   setSession(s)    called by the sign-in page once SRP succeeds
//   getAccessToken() a fresh access token (refreshed if expired or close to it)
//
// Freshness: the session is refreshed shortly before the access token expires,
// whenever the tab becomes visible again, and — because a timer can be late
// after a laptop sleeps — every request to REACT_APP_API_URL that carries a
// Bearer header has it swapped for a fresh token just before it is sent. The same
// requests carry the active shared company, if any (teamCompany.js).
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { globalSignOut, loadSession, REFRESH_MARGIN_S, sessionLive, signOut as oleneSignOut, toAuthUser } from "./olene";
import { IS_DEV, isStaff, PROD_APP_URL } from "../stage";
import { APP_NAME } from "./messages";
import { withSharedCompany } from "./teamCompany";
import { hubFor, showTransition, signOutEverywhereUrl, watchSignOut } from "./oleneSso";
import { STAGE } from "../stage";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL;

export function AuthProvider({ children }) {
  const [state, setState] = useState({ isLoading: true, user: null, error: null });
  const [devOnly, setDevOnly] = useState(false);
  const inflight = useRef(null);

  const applySession = useCallback((session) => {
    const next = toAuthUser(session);
    setState((prev) =>
      !prev.isLoading && prev.user?.access_token === next?.access_token
        ? prev
        : { isLoading: false, user: next, error: null });
  }, []);

  // One refresh at a time; concurrent callers share it.
  const refresh = useCallback(() => {
    if (!inflight.current) {
      inflight.current = loadSession()
        .then((session) => {
          applySession(session);
          return session;
        })
        .finally(() => {
          inflight.current = null;
        });
    }
    return inflight.current;
  }, [applySession]);

  const getAccessToken = useCallback(async () => {
    const session = await refresh();
    return session ? session.getAccessToken().getJwtToken() : null;
  }, [refresh]);

  // Initial load. Also drops tokens react-oidc-context left in localStorage.
  useEffect(() => {
    try {
      Object.keys(localStorage)
        .filter((k) => k.startsWith("oidc."))
        .forEach((k) => localStorage.removeItem(k));
    } catch {}
    refresh();
  }, [refresh]);

  // Refresh before expiry.
  const expiresAt = state.user?.expires_at;
  useEffect(() => {
    if (!expiresAt) return undefined;
    const ms = Math.max(0, (expiresAt - REFRESH_MARGIN_S) * 1000 - Date.now()) + 1000;
    const t = setTimeout(refresh, ms);
    return () => clearTimeout(t);
  }, [expiresAt, refresh]);

  // Refresh when the tab comes back (also notices a sign-out in another tab).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  // Swap in a fresh access token on every authenticated API request.
  useEffect(() => {
    if (!API_URL) return undefined;
    const previous = window.fetch;
    window.fetch = async (input, init) => {
      const url = typeof input === "string" ? input : input instanceof URL ? input.href : null;
      if (url && url.startsWith(API_URL) && init && init.headers) {
        const headers = new Headers(init.headers);
        if ((headers.get("Authorization") || "").startsWith("Bearer ")) {
          const token = await getAccessToken();
          if (token) {
            headers.set("Authorization", `Bearer ${token}`);
            init = { ...init, headers };
          }
          // A company shared with this person (teamCompany.js).
          if (typeof input === "string" || input instanceof URL) input = withSharedCompany(url);
        }
      }
      const res = await previous(input, init);
      // A staff support session ended elsewhere (backend acting.py): hooks/useCompany.js leaves it.
      if (res.status === 403 && url && url.startsWith(API_URL)) {
        res.clone().json().then((body) => {
          if (body?.detail?.code === "support_ended") window.dispatchEvent(new Event("payclear:support-ended"));
        }).catch(() => {});
      }
      // The API refuses non-staff on dev sites (403 dev_only); see stage.js.
      if (IS_DEV && res.status === 403 && url && url.startsWith(API_URL)) {
        res.clone().json().then((body) => {
          if (body?.detail?.code === "dev_only" || body?.code === "dev_only") setDevOnly(true);
        }).catch(() => {});
      }
      return res;
    };
    return () => {
      window.fetch = previous;
    };
  }, [getAccessToken]);

  // Dev sites: an Olene account outside olene-admin is signed out and sent to the real app.
  const accessToken = state.user?.access_token;
  useEffect(() => {
    if (IS_DEV && accessToken && !isStaff(accessToken)) setDevOnly(true);
  }, [accessToken]);
  useEffect(() => {
    if (!devOnly) return undefined;
    const t = setTimeout(async () => {
      try { await oleneSignOut(); } catch {}
      window.location.assign(PROD_APP_URL);
    }, 2500);
    return () => clearTimeout(t);
  }, [devOnly]);

  // Signed out in another Olene app: signed out here too (oleneSso.js).
  const signedIn = !!state.user;
  useEffect(() => {
    if (!signedIn) return undefined;
    return watchSignOut(sessionLive, async () => {
      await oleneSignOut();
      try { localStorage.removeItem("activeCompanyId"); } catch {}
      window.location.assign("/signin");
    });
  }, [signedIn]);

  const signOut = useCallback(async () => {
    showTransition("Signing you out…");
    // Every Olene app: revokes every session, here and everywhere.
    await globalSignOut();
    try {
      localStorage.removeItem("activeCompanyId");
    } catch {}
    setState({ isLoading: false, user: null, error: null });
    // Out of every Olene app (account.olenesoft.com revokes them all), then a full load of the
    // sign-in page, which resets every provider's in-memory state.
    window.location.assign(signOutEverywhereUrl("payclear", hubFor(STAGE), "/"));
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      isAuthenticated: !!state.user,
      signOut,
      setSession: applySession,
      getAccessToken,
    }),
    [state, signOut, applySession, getAccessToken]
  );

  if (devOnly) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <p role="status" className="max-w-[400px] text-center text-gray-700">
          This is {APP_NAME}'s development site, for Olene staff. Taking you to {APP_NAME}…
        </p>
      </div>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
